import { useState } from "react";
import { useForm } from "react-hook-form";
import { Alert, Box, Button, Stack, TextField, Typography } from "@mui/material";
import authService from "@/services/authService";
import { extractErrorMessage } from "@/utils/errors";

interface ChangePasswordFormValues {
  current_password: string;
  new_password: string;
  confirm_password: string;
}

const PASSWORD_PATTERN =
  /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^A-Za-z0-9]).{8,}$/;

export function ChangePasswordPage() {
  const [serverError, setServerError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const {
    register,
    handleSubmit,
    watch,
    reset,
    formState: { errors },
  } = useForm<ChangePasswordFormValues>();

  const newPassword = watch("new_password");

  const onSubmit = async (values: ChangePasswordFormValues) => {
    setServerError(null);
    setSuccess(false);
    setIsSubmitting(true);
    try {
      await authService.changePassword(values.current_password, values.new_password);
      setSuccess(true);
      reset();
    } catch (err) {
      setServerError(extractErrorMessage(err));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Box maxWidth={420}>
      <Typography variant="h4" fontWeight={700} gutterBottom>
        Change Password
      </Typography>

      <Box component="form" onSubmit={handleSubmit(onSubmit)} noValidate>
        <Stack spacing={2}>
          {serverError && <Alert severity="error">{serverError}</Alert>}
          {success && <Alert severity="success">Password changed successfully.</Alert>}

          <TextField
            label="Current password"
            type="password"
            fullWidth
            error={Boolean(errors.current_password)}
            helperText={errors.current_password?.message}
            {...register("current_password", { required: "Current password is required" })}
          />

          <TextField
            label="New password"
            type="password"
            fullWidth
            error={Boolean(errors.new_password)}
            helperText={
              errors.new_password?.message ??
              "Min 8 characters, with uppercase, lowercase, digit & special character"
            }
            {...register("new_password", {
              required: "New password is required",
              pattern: { value: PASSWORD_PATTERN, message: "Password does not meet complexity rules" },
            })}
          />

          <TextField
            label="Confirm new password"
            type="password"
            fullWidth
            error={Boolean(errors.confirm_password)}
            helperText={errors.confirm_password?.message}
            {...register("confirm_password", {
              required: "Please confirm your new password",
              validate: (value) => value === newPassword || "Passwords do not match",
            })}
          />

          <Box>
            <Button type="submit" variant="contained" disabled={isSubmitting}>
              {isSubmitting ? "Changing..." : "Change Password"}
            </Button>
          </Box>
        </Stack>
      </Box>
    </Box>
  );
}

export default ChangePasswordPage;
